// src/components/editor/VariableManager.jsx
import React, { useState, useCallback } from 'react';
import { Plus, Edit3, Trash2, Tag, Save, X } from 'lucide-react';

const VariableManager = ({ variables, onUpdateVariables }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    defaultValue: '',
    category: 'prix',
    type: 'text'
  });

  const categories = [
    { value: 'prix', label: 'Prix', color: 'green' },
    { value: 'info', label: 'Information', color: 'blue' },
    { value: 'promotion', label: 'Promotion', color: 'orange' },
    { value: 'temps', label: 'Temporel', color: 'purple' },
    { value: 'custom', label: 'Personnalisé', color: 'gray' }
  ];

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      defaultValue: '',
      category: 'prix',
      type: 'text'
    });
    setEditingVariable(null);
    setShowForm(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const variableData = {
      ...formData,
      id: editingVariable ? editingVariable.id : `var_${Date.now()}`,
    };

    const updatedVariables = editingVariable
      ? variables.map(v => v.id === editingVariable.id ? variableData : v)
      : [...variables, variableData];

    onUpdateVariables(updatedVariables);
    resetForm();
  };

  const handleEdit = (variable) => {
    setFormData(variable);
    setEditingVariable(variable);
    setShowForm(true);
  };

  const handleDelete = (variableId) => {
    if (window.confirm('Supprimer cette variable ?')) {
      const updatedVariables = variables.filter(v => v.id !== variableId);
      onUpdateVariables(updatedVariables);
    }
  };

  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category) || categories[0];
    return {
      green: 'bg-green-100 text-green-700',
      blue: 'bg-blue-100 text-blue-700',
      orange: 'bg-orange-100 text-orange-700',
      purple: 'bg-purple-100 text-purple-700',
      gray: 'bg-gray-100 text-gray-700'
    }[cat.color];
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Variables Disponibles</h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Nouvelle Variable</span>
        </button>
      </div>

      {/* Liste des variables */}
      <div className="grid grid-cols-1 gap-3">
        {variables.map(variable => (
          <div key={variable.id} className="p-3 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">{variable.name}</span>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded text-xs ${getCategoryStyle(variable.category)}`}>
                  {categories.find(c => c.value === variable.category)?.label}
                </span>
                <button
                  onClick={() => handleEdit(variable)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Edit3 className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleDelete(variable.id)}
                  className="p-1 hover:bg-red-100 rounded text-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-2">{variable.description}</p>
            <div className="text-sm">
              <span className="text-gray-500">ID: </span>
              <code className="bg-gray-100 px-1 rounded text-xs">{variable.id}</code>
            </div>
            <div className="text-sm mt-1">
              <span className="text-gray-500">Défaut: </span>
              <span className="font-medium">{variable.defaultValue}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Formulaire modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingVariable ? 'Modifier' : 'Nouvelle'} Variable
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">ID (unique)</label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.replace(/[^a-z0-9_]/g, '') }))}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  placeholder="ex: price_menu_jour"
                  required
                  disabled={!!editingVariable}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Valeur par défaut</label>
                <input
                  type="text"
                  value={formData.defaultValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="ex: 4€20"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Catégorie</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingVariable ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariableManager;